import { Button, Flex } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "components/RFQ/Table";
import { SkeletonRow } from "components/SkeletonRow";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { DEFAULT_IMAGE_AVATAR } from "utils/constants";

type PreviewSupplierProps = {
	isLoading: boolean;
	rfx: Rfx;
};

type SupplierDisplay = {
	id: number;
	contact_name: string;
	email: string;
	is_guest: boolean;
	logo_path: string;
	name: string;
};

const getInvitedSuppliers = (rfx: Rfx): SupplierDisplay[] => {
	if (!rfx?.supplierInvited || !rfx?.supplierList?.suppliers) {
		return [];
	}

	//@ts-ignore
	const invitedSuppliers = rfx?.supplierInvited?.all;

	if (invitedSuppliers?.length === 0) {
		return;
	}

	const supplierData = invitedSuppliers.map((invite) => {
		if (invite.is_guest) {
			return {
				contact_name: invite.name,
				email: invite.email,
				id: invite.id,
				name: invite.name,
				is_guest: true,
			} as SupplierDisplay;
		}

		const supplier = rfx.supplierList.suppliers.find(
			(supplier) => supplier.id === invite.company_id
		);

		return supplier
			? ({
				contact_name: supplier.contact_name,
				email: supplier.email,
				name: supplier.name,
				id: supplier.id,
				logo_path: supplier.logo_path,
				is_guest: false,
			} as SupplierDisplay)
			: null;
	});

	return supplierData as SupplierDisplay[];
};

export const PreviewSuppliers: React.FC<PreviewSupplierProps> = ({
	rfx,
	isLoading,
}) => {
	const invitedSuppliers = getInvitedSuppliers(rfx);

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead firstCol={true}>
						<div
							className="flex"
							style={{ gap: "8px", alignItems: "center" }}
						>
							Supplier
						</div>
					</TableHead>
					<TableHead> </TableHead>
					<TableHead className={"max-w-[400px]"}>Contact Name</TableHead>
					<TableHead className={"max-w-[218px]"}>Email</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{isLoading && (
					<>
						<SkeletonRow />
						<SkeletonRow />
						<SkeletonRow />
					</>
				)}
				{!isLoading && invitedSuppliers?.length > 0 ? (
					invitedSuppliers.map((supplier, index) => (
						<TableRow key={supplier?.id}>
							<TableCell className={"min-w-[250px]"}>
								<div
									className="flex gap-3"
									style={{
										alignItems: "center",
										gap: "8px",
									}}
								>
									<div className="logo-area-wrapper">
										<div
											className={`h-12 w-12 content-center rounded-full bg-white text-center font-medium text-utility-gray-700 drop-shadow-md`}
										>
											{supplier?.is_guest ? (
												<Icon
													icon="guest-invite"
													className="p-3"
												/>
											) : (
												<FallbackImage
													src={supplier?.logo_path ?? DEFAULT_IMAGE_AVATAR}
													alt={`${supplier?.name || "-"} Logo`}
													className="block h-full w-full rounded-full object-contain"
													fallBackClassName="p-2"
													fallbackSrc={DEFAULT_IMAGE_AVATAR}
												/>
											)}
										</div>
									</div>
									<p className="title">
										{supplier?.is_guest ? "Guest Invite" : supplier?.name}
									</p>
								</div>
							</TableCell>
							<TableCell>
								<div>
									{!supplier?.is_guest && (
										<Button
											className="btn-company-profile-wrapper"
											as="a"
											target="_blank"
											href={`/company/${supplier?.id}`}
											style={{ border: "none" }}
										>
											<Flex
												gap={2}
												className="btn-company-profile items-center"
											>
												View Company Profile
											</Flex>
										</Button>
									)}
								</div>
							</TableCell>
							<TableCell>
								<p className="text">{supplier?.contact_name || "-"}</p>
							</TableCell>
							<TableCell>
								<p className="text">
									{supplier?.email ? (
										<a href={`mailto:${supplier?.email}`}>{supplier?.email}</a>
									) : (
										"-"
									)}
								</p>
							</TableCell>
						</TableRow>
					))
				) : (
					<TableRow className="col-span-5 h-[500px]">
						<div className="absolute left-2/4 -translate-x-2/4">
							No Suppliers have been selected for this RFx
						</div>
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
};
